import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Shield, LayoutDashboard, ClipboardList, Settings, LogOut,
  Users, FileText, ChevronRight, Bell, Menu, X, BarChart2, Clock, ClipboardCheck
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", roles: ["admin", "underwriter", "loss_control"] },
  { label: "Order Inspection", icon: ClipboardList, path: "/dashboard/order", roles: ["admin", "underwriter", "loss_control"] },
  { label: "Recommendations", icon: ClipboardCheck, path: "/dashboard/recommendations", roles: ["admin", "underwriter", "loss_control", "lc_manager"] },
  { label: "Analytics", icon: BarChart2, path: "/analytics", roles: ["admin", "underwriter", "loss_control"] },
  { label: "LC Module", icon: Users, path: "/lc-dashboard", roles: ["lc_manager", "lc_staff", "admin"] },
  { label: "Admin Portal", icon: Settings, path: "/admin", roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout, sessionWarning, warnCountdown, resetIdleTimer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const filteredNav = navItems.filter(item => item.roles.includes(user?.role));

  const fmtCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-navy flex flex-col transform transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static md:flex`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
          <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-base leading-tight">RiskSurvey</div>
            <div className="text-white/50 text-xs">.ai</div>
          </div>
          <button className="ml-auto md:hidden text-white/60 hover:text-white" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-6 space-y-1">
          {filteredNav.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  active ? "bg-white/15 text-white border-r-2 border-white" : "text-white/70 hover:text-white hover:bg-white/10"
                }`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
                {active && <ChevronRight className="w-3 h-3 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-1">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-semibold">{user?.name?.[0]?.toUpperCase() || "U"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate">{user?.name}</div>
              <div className="text-white/50 text-xs capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg text-sm transition-colors duration-150"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 md:px-6 h-14 flex items-center gap-4 sticky top-0 z-30">
          <button className="md:hidden text-slate-500 hover:text-slate-700" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="ml-auto flex items-center gap-3">
            <button className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors duration-150">
              <Bell className="w-4 h-4" />
            </button>
            <div className="text-sm text-slate-500">
              <span className="font-medium text-slate-700">{user?.name}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 animate-fade-in">
          {children}
        </main>
      </div>

      {/* Session Timeout Warning Modal */}
      {sessionWarning && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="session-timeout-modal">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full animate-slide-up">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-heading font-bold text-slate-800 text-lg text-center mb-2">Session Expiring Soon</h3>
            <p className="text-slate-500 text-sm text-center mb-1">
              You've been inactive. For your security, you'll be logged out in:
            </p>
            <p className="text-3xl font-heading font-bold text-amber-600 text-center mb-6" data-testid="session-countdown">
              {fmtCountdown(warnCountdown)}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { handleLogout(); }}
                className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                data-testid="session-logout-btn"
              >
                Log Out
              </button>
              <button
                onClick={resetIdleTimer}
                className="flex-1 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors"
                data-testid="session-continue-btn"
              >
                Stay Signed In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
